import { http, qs } from "../api";
import { Banner, Empty, useAsync } from "../ui";
import { BarChartCard, FunnelChart, StatTile, type BarDatum } from "../charts";
import { Card, Loadable, dash, fmtInt, fmtMs, fmtPct, num, rateColor } from "./format";
import type { FunnelStep, StageRow, SuiteDetail } from "./types";

// ============================================================ lanes tab

const PROD_HOURS = 24;

/**
 * Which responder served the turn, and what that lane cost. The latency columns
 * are why this is separate from the grid: a lane can be at 100% pass and still
 * be the reason p95 is 20 seconds, and no pass-rate view will ever show that.
 */
export function LanesTab({ detail }: { detail: SuiteDetail | null }) {
  const lanes = detail?.lanes ?? [];
  const source = detail?.lane_source ?? {};
  const runLatency = detail?.latency ?? {};

  const stages = useAsync<StageRow[]>(
    () => http.get<StageRow[]>(`/chatbot/eval-ops/telemetry/stages${qs({ hours: PROD_HOURS })}`),
    []
  );
  const funnel = useAsync<FunnelStep[]>(
    () => http.get<FunnelStep[]>(`/chatbot/eval-ops/telemetry/funnel${qs({ hours: PROD_HOURS })}`),
    []
  );

  const laneShare: BarDatum[] = lanes.map((l) => ({ label: l.lane || "(blank)", value: num(l.n) }));
  const laneTail: BarDatum[] = lanes.map((l) => ({ label: l.lane || "(blank)", value: num(l.latency?.p95_ms) }));
  const stageBars: BarDatum[] = (stages.data ?? []).map((s) => ({
    label: String(s.stage ?? "(blank)"),
    value: num(s.p95_ms),
  }));
  const funnelStages = (funnel.data ?? []).map((f) => ({ label: f.stage, count: num(f.count) }));

  return (
    <div className="stack">
      <Card
        title="Run latency"
        note={`Measured per turn by the harness over ${fmtInt(
          runLatency.n
        )} timed turns. The tail is reported beside the median because a p50 alone hides the turns a user actually waits through.`}
      >
        {!runLatency.instrumented ? (
          <Banner tone="warning">No turn in this run recorded a latency — nothing here is measurable.</Banner>
        ) : (
          <div className="grid c4">
            <StatTile label="avg" value={fmtMs(runLatency.avg_ms)} />
            <StatTile label="p50" value={fmtMs(runLatency.p50_ms)} />
            <StatTile label="p90" value={fmtMs(runLatency.p90_ms)} />
            <StatTile label="p95" value={fmtMs(runLatency.p95_ms)} />
            <StatTile label="p99" value={fmtMs(runLatency.p99_ms)} />
            <StatTile label="max" value={fmtMs(runLatency.max_ms)} />
          </div>
        )}
      </Card>

      <Card
        title="Serving lanes"
        note={
          <>
            Grouped by <span className="strong">{dash(source.column)}</span> — the field this run actually recorded
            for the serving path.
          </>
        }
      >
        {source.note && <Banner tone="warning">{source.note}</Banner>}
        {!lanes.length ? (
          <Empty title="No lanes recorded" body="This run wrote no serving-path field." />
        ) : (
          <>
            <div className="grid c2">
              <BarChartCard title="Turns by lane" data={laneShare} horizontal yLabel="turns" />
              <BarChartCard title="p95 latency by lane" data={laneTail} horizontal note="Milliseconds." />
            </div>
            <div className="table-wrap" style={{ marginTop: "var(--sp-3)" }}>
              <table className="data">
                <thead>
                  <tr>
                    <th>Lane</th>
                    <th className="num">Turns</th>
                    <th className="num">Share</th>
                    <th className="num">Pass</th>
                    <th className="num">LLM turns</th>
                    <th className="num">Tokens</th>
                    <th className="num">p50</th>
                    <th className="num">p95</th>
                    <th className="num">tail</th>
                  </tr>
                </thead>
                <tbody>
                  {lanes.map((lane) => (
                    <tr key={lane.lane || "(blank)"}>
                      <td>{lane.lane || "(blank)"}</td>
                      <td className="num">{fmtInt(lane.n)}</td>
                      <td className="num">{fmtPct(lane.share, 0)}</td>
                      <td className="num" style={{ color: rateColor(lane.pass_rate, num(lane.n)) }}>
                        {fmtPct(lane.pass_rate, 0)}{" "}
                        <span className="muted">
                          ({fmtInt(lane.passed)}/{fmtInt(lane.n)})
                        </span>
                      </td>
                      <td className="num" style={{ color: source.llm_instrumented ? undefined : "var(--warning)" }}>
                        {source.llm_instrumented ? fmtInt(lane.llm_turns) : "—"}
                      </td>
                      <td className="num" style={{ color: detail?.tokens?.instrumented ? undefined : "var(--warning)" }}>
                        {detail?.tokens?.instrumented ? fmtInt(lane.tokens) : "—"}
                      </td>
                      <td className="num">{fmtMs(lane.latency?.p50_ms)}</td>
                      <td className="num">{fmtMs(lane.latency?.p95_ms)}</td>
                      <td className="num muted">
                        {fmtMs(lane.latency?.p99_ms)} · {fmtMs(lane.latency?.max_ms)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {!detail?.tokens?.instrumented && detail?.tokens?.note && (
              <p style={{ fontSize: 11, color: "var(--warning)" }}>{detail.tokens.note}</p>
            )}
          </>
        )}
      </Card>

      <Card
        title={`Production pipeline — last ${PROD_HOURS}h`}
        note="Live traffic, not this run. Per-stage latency inside a turn, and the received → graph → banded → detected funnel. It is here because it is the level below a lane; it is labelled because it is a different population."
      >
        <div className="grid c2">
          <div>
            <Loadable loading={stages.loading} error={stages.error} what="Failed to load stage spans" cols={5}>
              {!(stages.data ?? []).length ? (
                <Empty title="No stage spans" body="Nothing was recorded in this window." />
              ) : (
                <>
                  <BarChartCard title="Stage p95" data={stageBars} horizontal note="Milliseconds, slowest tail first." />
                  <div className="table-wrap">
                    <table className="data">
                      <thead>
                        <tr>
                          <th>Stage</th>
                          <th className="num">n</th>
                          <th className="num">p50</th>
                          <th className="num">p95</th>
                          <th className="num">errors</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(stages.data ?? []).map((stage) => (
                          <tr key={String(stage.stage)}>
                            <td>{dash(stage.stage)}</td>
                            <td className="num">{fmtInt(stage.n)}</td>
                            <td className="num">{fmtMs(stage.p50_ms)}</td>
                            <td className="num">{fmtMs(stage.p95_ms)}</td>
                            <td className="num" style={{ color: num(stage.errors) > 0 ? "var(--critical)" : undefined }}>
                              {fmtInt(stage.errors)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </Loadable>
          </div>
          <div>
            <Loadable loading={funnel.loading} error={funnel.error} what="Failed to load the detection funnel" cols={2}>
              {!funnelStages.length ? (
                <Empty title="No classification turns" body="Nothing was classified in this window." />
              ) : (
                <FunnelChart
                  stages={funnelStages}
                  title="Detection funnel"
                  note="received → graph_hit → banded → scam_detected"
                />
              )}
            </Loadable>
          </div>
        </div>
      </Card>
    </div>
  );
}
